import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Shell } from "@/components/Shell";
import { StatusPill } from "@/pages/Home";
import { money, fmtDate, DOC_META } from "@/lib/format";
import { generateDocPdf, downloadPdf, printPdf, sharePdf } from "@/lib/pdf";
import { Download, Printer, Share2, ArrowRightLeft, Pencil, Trash2, CheckCircle2, Check, Lock } from "lucide-react";
import { ConfirmSheet } from "@/components/ConfirmSheet";

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);

  const load = () => api.get(`/documents/${id}`).then(({ data }) => setDoc(data.document)).catch(() => navigate("/home"));
  useEffect(() => { load(); api.get("/me/logo-data").then(({ data }) => setLogo(data.data_url)).catch(() => {}); }, [id]);

  if (!doc) return <Shell back title="Loading…"><div className="h-64 bg-surfaceTertiary rounded-[12px] animate-pulse" /></Shell>;
  const meta = DOC_META[doc.type] || {};
  const d = doc.data || {};
  const filename = `${doc.number}_${(doc.client?.name || "docket").replace(/\s+/g, "-")}.pdf`;

  const pdf = () => generateDocPdf(doc, user, logo);
  const onShare = async () => { const r = await sharePdf(pdf(), filename, `${meta.label} ${doc.number} from ${user.business_name}`); if (r === "downloaded") toast.info("Sharing not supported — downloaded instead"); };

  const convert = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/documents/${id}/convert`); toast.success("Invoice created from quote"); navigate(`/doc/${data.document.id}`, { replace: true }); }
    catch (e) { if (e?.response?.status === 402) navigate("/paywall"); else toast.error("Convert failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => { await api.patch(`/documents/${id}`, { status }); toast.success(`Marked ${status}`); load(); };
  const del = async () => { setDelBusy(true); try { await api.delete(`/documents/${id}`); toast.success("Deleted"); navigate(-1); } catch { toast.error("Couldn't delete"); setDelBusy(false); } };
  const saveDone = async () => { setBusy(true); try { await api.patch(`/documents/${id}`, { data: doc.data }); toast.success("Saved to your recents"); navigate("/home"); } catch { toast.error("Save failed"); } finally { setBusy(false); } };

  return (
    <Shell back title={`${meta.label} ${doc.number}`} showNav={false} right={<StatusPill status={doc.status} />}>
      {/* Action bar */}
      <div className="grid grid-cols-3 gap-2 mb-4 animate-fade-up">
        <ActionBtn testid="pdf-download" icon={Download} label="Download" onClick={() => { downloadPdf(pdf(), filename); toast.success("PDF downloaded"); }} />
        <ActionBtn testid="pdf-share" icon={Share2} label="Share" onClick={onShare} primary />
        <ActionBtn testid="pdf-print" icon={Printer} label="Print" onClick={() => printPdf(pdf())} />
      </div>

      {/* Preview */}
      <div className="bg-surface rounded-[16px] border border-border overflow-hidden animate-fade-up">
        <div className="h-1.5 bg-brandPrimary" />
        <div className="p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {logo && <img src={logo} alt="" className="w-12 h-12 object-contain rounded-[8px]" />}
              <div>
                <p className="font-heading font-extrabold text-onSurface leading-tight">{user?.business_name}</p>
                <p className="text-xs text-muted-foreground">{user?.trade_label}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-heading text-lg font-extrabold text-brandPrimary uppercase">{meta.label}</p>
              <p className="text-xs text-muted-foreground">{doc.number}</p>
              <p className="text-xs text-muted-foreground">{fmtDate(doc.created_at)}</p>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-border">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Prepared for</p>
            <p className="font-heading font-bold text-onSurface">{doc.client?.name || "—"}</p>
            {doc.client?.address && <p className="text-xs text-muted-foreground">{doc.client.address}</p>}
          </div>

          {d.title && <p className="mt-4 font-heading font-bold text-onSurface">{d.title}</p>}

          {doc.type !== "waste_note" ? (
            <div className="mt-3 space-y-2">
              {(d.line_items || []).map((li, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-onSurface flex-1 pr-3">{li.description} <span className="text-muted-foreground">× {li.qty}</span></span>
                  <span className="font-semibold text-onSurface">{money(Number(li.qty) * Number(li.unit_price))}</span>
                </div>
              ))}
              <div className="pt-2 mt-1 border-t border-border space-y-1">
                <div className="flex justify-between text-sm text-muted-foreground"><span>Subtotal</span><span>{money(d.subtotal)}</span></div>
                {Number(d.tax_rate) > 0 && <div className="flex justify-between text-sm text-muted-foreground"><span>VAT ({d.tax_rate}%)</span><span>{money(d.tax)}</span></div>}
                <div className="flex justify-between font-heading font-extrabold text-onSurface"><span>Total</span><span className="text-brandPrimary">{money(d.total)}</span></div>
              </div>
            </div>
          ) : (
            <div className="mt-3 space-y-1.5 text-sm">
              <Row k="Waste" v={d.description} />
              <Row k="EWC code" v={d.ewc_code} />
              <Row k="Quantity" v={d.quantity} />
              <Row k="Containment" v={d.container === "container" ? `Container — ${d.container_type || ""}` : "Loose"} />
              <Row k="Transfer date" v={fmtDate(d.date_of_transfer)} />
              <Row k="Place" v={d.place_of_transfer} />
            </div>
          )}

          {d.notes && <p className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">{d.notes}</p>}
          {doc.type === "quote" && d.valid_until && <p className="mt-2 text-xs text-muted-foreground">Valid until {fmtDate(d.valid_until)}</p>}
          {doc.type === "invoice" && d.due_date && <p className="mt-2 text-xs text-muted-foreground">Due {fmtDate(d.due_date)}</p>}
        </div>
      </div>

      {/* Secondary actions */}
      <div className="mt-4 space-y-2">
        {doc.type === "quote" && (
          <button data-testid="convert-btn" disabled={busy} onClick={convert} className="w-full bg-onSurface text-onBrand rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50">
            <ArrowRightLeft size={18} /> Convert to invoice
          </button>
        )}
        <div className="flex gap-2">
          {doc.type === "invoice" && doc.status !== "paid" && <button data-testid="mark-paid" onClick={() => setStatus("paid")} className="flex-1 bg-surface border border-border rounded-full h-11 text-sm font-semibold flex items-center justify-center gap-1.5 text-success active:scale-95 transition-transform"><CheckCircle2 size={16} /> Mark paid</button>}
          {doc.type === "quote" && doc.status === "draft" && <button data-testid="mark-sent" onClick={() => setStatus("sent")} className="flex-1 bg-surface border border-border rounded-full h-11 text-sm font-semibold active:scale-95 transition-transform">Mark sent</button>}
          <button data-testid="edit-doc" onClick={() => navigate(`/edit/${id}`)} className="flex-1 bg-surface border border-border rounded-full h-11 text-sm font-semibold flex items-center justify-center gap-1.5 active:scale-95 transition-transform"><Pencil size={15} /> Edit</button>
        </div>
        {doc.type === "waste_note" ? (
          <p className="w-full text-muted-foreground text-xs flex items-center justify-center gap-1.5 py-3"><Lock size={13} /> Kept for 2+ years — waste notes can't be deleted</p>
        ) : (
          <button data-testid="delete-doc" onClick={() => setConfirmDel(true)} className="w-full text-error text-sm font-semibold flex items-center justify-center gap-2 py-3"><Trash2 size={16} /> Delete document</button>
        )}
      </div>

      <ConfirmSheet
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={del}
        busy={delBusy}
        title="Delete this document?"
        message={`${meta.label} ${doc.number}${doc.client?.name ? ` for ${doc.client.name}` : ""} will be permanently deleted. This can't be undone.`}
        testid="doc-detail-delete-confirm"
      />

      <div className="h-24" />
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-surface/95 backdrop-blur-md border-t border-border p-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}>
        <button data-testid="doc-detail-save-btn" disabled={busy} onClick={saveDone} className="w-full bg-brandPrimary text-onBrand rounded-full h-13 py-3.5 font-heading font-bold flex items-center justify-center gap-2 hover:bg-brandSecondary transition-colors active:scale-[0.98] disabled:opacity-50">
          <Check size={20} /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </Shell>
  );
}

const Row = ({ k, v }) => <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="text-onSurface text-right font-medium">{v || "—"}</span></div>;

const ActionBtn = ({ icon: Icon, label, onClick, testid, primary }) => (
  <button data-testid={testid} onClick={onClick} className={`flex flex-col items-center gap-1.5 rounded-[12px] py-3 font-semibold text-xs active:scale-95 transition-transform ${primary ? "bg-brandPrimary text-onBrand" : "bg-surface border border-border text-onSurface"}`}>
    <Icon size={20} /> {label}
  </button>
);
